import { classNames } from "Utils/utils";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import AddressAutoComplete from "components/common/GoogleMap/AddressAutoComplete";
import { AiOutlineWarning } from "react-icons/ai";
import { INCIDENT_CATEGORIES } from "../../constants";
import { YesNoToggle } from "../forms/YesNoToggle";
import useCareLabels from "hooks/useCareLabels";

export const IncidentOverviewStep = ({
  incidentCategory,
  setIncidentCategory,
  incidentCategoryOthers,
  setIncidentCategoryOthers,
  personInvolvedVisitor,
  setPersonInvolvedVisitor,
  personInvolvedVolunteer,
  setPersonInvolvedVolunteer,
  personInvolvedOther,
  setPersonInvolvedOther,
  incidentOverviewDate,
  setIncidentOverviewDate,
  incidentOverviewTime,
  setIncidentOverviewTime,
  validationAttempted,
  register,
  watch,
  setValue,
  errors,
}) => {
  const { participantLabel } = useCareLabels();
  const hasOthersIncidentCategory = incidentCategory.some(
    (c) => c.value === "others"
  );

  // Validation states (only show errors if validation was attempted)
  const showIncidentCategoryError =
    validationAttempted && incidentCategory.length === 0;
  const injuriesOccurred = watch("injuries_occurred");
  const showInjuriesError = validationAttempted && !injuriesOccurred;
  const showPersonResponsibleNotifiedError =
    validationAttempted && !watch("person_responsible_notified");
  const showDateError = validationAttempted && !incidentOverviewDate;
  const showTimeError = validationAttempted && !incidentOverviewTime;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}

      {/* A. People Involved (Text Input Grid) */}
      <section className="border-t border-gray-100 pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">
          People Involved
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Visitor{" "}
              <span className="text-gray-500 font-normal">(if any)</span>
            </label>
            <input
              type="text"
              value={personInvolvedVisitor}
              onChange={(e) => setPersonInvolvedVisitor(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
              placeholder="Enter visitor name/role..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Volunteer{" "}
              <span className="text-gray-500 font-normal">(if any)</span>
            </label>
            <input
              type="text"
              value={personInvolvedVolunteer}
              onChange={(e) => setPersonInvolvedVolunteer(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
              placeholder="Enter volunteer name/role..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Other <span className="text-gray-500 font-normal">(if any)</span>
            </label>
            <input
              type="text"
              value={personInvolvedOther}
              onChange={(e) => setPersonInvolvedOther(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
              placeholder="Enter other person name/role..."
            />
          </div>
        </div>
      </section>

      {/* B. Location & Timing (2-Column Grid) */}
      <section className="border-t border-gray-100 pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">
          Location & Timing
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Date of incident <span className="text-red-500">*</span>
            </label>
            <DateRangePickerNew
              isSingleDate
              singleSelectedDate={
                incidentOverviewDate ? new Date(incidentOverviewDate) : null
              }
              handleChange={(date) => {
                setIncidentOverviewDate(date);
                setValue("incident_overview_date", date);
              }}
              handleCancel={() => {
                setIncidentOverviewDate(null);
                setValue("incident_overview_date", "");
              }}
              maxDate={new Date()}
              placeholder="Select date"
            />
            {showDateError && (
              <p className="text-red-500 text-xs mt-1">Date is required</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Time of incident <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              {...register("incident_overview_time", { required: true })}
              value={incidentOverviewTime}
              onChange={(e) => {
                setIncidentOverviewTime(e.target.value);
                setValue("incident_overview_time", e.target.value);
              }}
              className={classNames(
                "w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm",
                showTimeError ? "border-red-300 bg-red-50" : "border-gray-300"
              )}
            />
            {showTimeError && (
              <p className="text-red-500 text-xs mt-1">Time is required</p>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Address of the incident
            </label>
            <AddressAutoComplete
              google={window?.google}
              customNames={{
                state: "incident_address_state",
                street_address: "incident_address_street",
                suburb: "incident_address_suburb",
                postcode: "incident_address_postcode",
              }}
              errors={errors}
            >
              <input
                type="text"
                id="address-input"
                {...register("incident_address_street")}
                className={classNames(
                  "w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm",
                  errors.incident_address_street
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                )}
                placeholder="Start typing address..."
              />
            </AddressAutoComplete>
            {errors.incident_address_street && (
              <p className="text-red-500 text-xs mt-1">Address is required</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Details of location of incident{" "}
              <span className="text-gray-500 font-normal text-xs">
                (e.g. participant home, kitchen, bedroom, café, shopping mall
                etc)
              </span>
            </label>
            <input
              type="text"
              {...register("incident_location_details")}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Enter specific location details..."
            />
          </div>
        </div>
      </section>

      {/* C. Incident Category (Multi-select Grid) */}
      <section className="border-t border-gray-100 pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">
          Incident Category{" "}
          <span className="text-gray-500 font-normal text-xs">
            (*PWD: person with disability)
          </span>{" "}
          <span className="text-red-500">*</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {INCIDENT_CATEGORIES.map((category) => {
            const isSelected = incidentCategory.some(
              (c) => c.value === category.value
            );
            return (
              <label
                key={category.value}
                className={classNames(
                  "flex items-start gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all text-sm",
                  isSelected
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : showIncidentCategoryError
                    ? "border-red-300 bg-red-50"
                    : "border-gray-200 hover:border-gray-300 text-gray-700"
                )}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setIncidentCategory([...incidentCategory, category]);
                    } else {
                      setIncidentCategory(
                        incidentCategory.filter(
                          (c) => c.value !== category.value
                        )
                      );
                      if (category.value === "others") {
                        setIncidentCategoryOthers("");
                      }
                    }
                  }}
                  className="w-4 h-4 text-blue-600 rounded mt-0.5 flex-shrink-0"
                />
                <span className="font-medium">{category.label}</span>
              </label>
            );
          })}
        </div>
        {showIncidentCategoryError && (
          <p className="text-red-500 text-xs mt-2">
            Please select at least one incident category
          </p>
        )}

        {/* Conditional: Others Input (Animated) */}
        {hasOthersIncidentCategory && (
          <div className="mt-4 animate-fadeIn">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Specify Details{" "}
              <span className="text-gray-500 font-normal text-xs">
                (if Others selected)
              </span>
            </label>
            <input
              type="text"
              value={incidentCategoryOthers}
              onChange={(e) => setIncidentCategoryOthers(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
              placeholder="Enter incident category details..."
            />
          </div>
        )}
      </section>

      {/* D. Injury Branching Logic (CRITICAL) */}
      <section className="border-t border-gray-100 pt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            Are there any injuries to the {participantLabel.toLowerCase()}?{" "}
            <span className="text-red-500">*</span>
          </h3>
          <YesNoToggle
            name="injuries_occurred"
            value={injuriesOccurred || ""}
            onChange={(val) => setValue("injuries_occurred", val)}
          />
        </div>
        {showInjuriesError && (
          <p className="text-red-500 text-xs mt-2">Please select Yes or No</p>
        )}
        <p className="text-xs text-red-600 mt-1">
          (if yes, please fill out the injury form as well)
        </p>
        {injuriesOccurred === "yes" && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800 animate-fadeIn">
            <AiOutlineWarning className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">
              Injury details will be collected in the next step.
            </span>
          </div>
        )}
      </section>

      {/* Person Responsible Notification */}
      <section className="border-t border-gray-100 pt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            Has the person responsible for the {participantLabel.toLowerCase()} been notified?{" "}
            <span className="text-red-500">*</span>
          </h3>
          <YesNoToggle
            name="person_responsible_notified"
            value={watch("person_responsible_notified") || ""}
            onChange={(val) => setValue("person_responsible_notified", val)}
          />
        </div>
        {showPersonResponsibleNotifiedError && (
          <p className="text-red-500 text-xs mt-2">Please select Yes or No</p>
        )}
      </section>

      {/* E. Compliance Toggles (Compact 3-Column Grid) */}
      <section className="border-t border-gray-100 pt-6">
        <h3 className="text-base font-bold text-gray-900 mb-1">Compliance</h3>
        <p className="text-xs text-gray-500 mb-4">
          Answer the following compliance questions
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { key: "first_aid_given", label: "Was first aid given?" },
            {
              key: "ambulance_needed",
              label: "Was there any need for an ambulance?",
            },
            { key: "prn_administered", label: "Was the PRN administered?" },
            {
              key: "reported_to_police",
              label: "Was this reported to the police?",
            },
            {
              key: "medical_review_offered",
              label: "Was medical review appointment offered?",
            },
          ].map((q) => (
            <div
              key={q.key}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <span className="text-sm font-medium text-gray-700 pr-2">
                {q.label}
              </span>
              <YesNoToggle
                name={q.key}
                value={watch(q.key)}
                onChange={(val) => setValue(q.key, val)}
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
